/**
 * Contact page component with dynamic form handling.
 * Provides contact form with conditional fields based on contact method selection.
 */

import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormControl, ReactiveFormsModule, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { StageComponent } from '../../components/stage/stage.component';
import { FormInputComponent } from '../../components/form-input/form-input.component';
import { FormCheckboxComponent } from '../../components/form-checkbox/form-checkbox.component';
import { FormRadioComponent } from '../../components/form-radio/form-radio.component';
import { ButtonComponent } from '../../components/button/button.component';
import { FormSelectComponent } from '../../components/form-select/form-select.component';
import { FormControlConfig } from '../../types/form.types';
import { VALIDATION_PATTERNS } from '../../types/validation.constants';
import { ApiService } from '../../services/api.service';
import { DialogService } from '../../services/dialog.service';
import { DataLayerService } from '../../services/data-layer.service';

/**
 * Contact page component with dynamic form handling.
 *
 * @remarks Provides a contact form that adapts based on the user's preferred
 * contact method (phone or written). Includes form validation, submission handling,
 * and integration with analytics tracking. Uses reactive forms with conditional
 * field requirements and custom validation patterns.
 */
@Component({
  selector: 'app-contact',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    StageComponent,
    FormInputComponent,
    FormCheckboxComponent,
    FormRadioComponent,
    ButtonComponent,
    FormSelectComponent,
  ],
  templateUrl: './contact.component.html',
  styleUrls: ['./contact.component.scss'],
})
export class ContactComponent implements OnInit {
  /** @remarks Main reactive form group for the contact form */
  contactForm!: FormGroup;

  /** @remarks Current status of form submission process */
  submissionStatus: 'idle' | 'submitting' | 'success' | 'error' = 'idle';

  /** @remarks Configuration for salutation radio button field */
  salutationConfig = new FormControlConfig({
    name: 'salutation',
    label: 'Anrede',
    required: true,
    errorMessage: 'Bitte wählen Sie eine Anrede',
    errorMessageRequired: 'Dieses Feld ist ein Pflichtfeld. Bitte ausfüllen.',
    type: 'radio',
    options: [
      { value: 'Herr', label: 'Herr' },
      { value: 'Frau', label: 'Frau' },
      { value: 'Divers', label: 'Divers' },
    ],
  });

  /** @remarks Configuration for first name input field */
  firstNameConfig = new FormControlConfig({
    name: 'firstName',
    label: 'Vorname',
    required: true,
    errorMessage: 'Der eingegebene Inhalt ist zu lang, bitte kürzen Sie diesen.',
    errorMessageRequired: 'Dieses Feld ist ein Pflichtfeld. Bitte ausfüllen.',
    type: 'text',
  });

  /** @remarks Configuration for last name input field */
  lastNameConfig = new FormControlConfig({
    name: 'lastName',
    label: 'Nachname',
    required: true,
    errorMessage: 'Der eingegebene Inhalt ist zu lang, bitte kürzen Sie diesen.',
    errorMessageRequired: 'Dieses Feld ist ein Pflichtfeld. Bitte ausfüllen.',
    type: 'text',
  });

  /** @remarks Configuration for contact method radio button field */
  contactMethodConfig = new FormControlConfig({
    name: 'contactMethod',
    label: 'Möchten Sie telefonisch oder schriftlich kontaktiert werden?',
    required: true,
    errorMessage: 'Bitte wählen Sie eine Kontaktart',
    errorMessageRequired: 'Dieses Feld ist ein Pflichtfeld. Bitte ausfüllen.',
    type: 'radio',
    options: [
      { value: 'phone', label: 'telefonisch' },
      { value: 'written', label: 'schriftlich' },
    ],
  });

  /** @remarks Configuration for phone number input field */
  phoneNumberConfig = new FormControlConfig({
    name: 'phoneNumber',
    label: 'Rufnummer',
    required: true,
    errorMessage: 'Bitte geben Sie eine gültige deutsche Telefonnummer ein',
    errorMessageRequired: 'Dieses Feld ist ein Pflichtfeld. Bitte ausfüllen.',
    placeholder: 'Rufnummer eingeben (z.B. +49 123 456789)',
    type: 'tel',
  });

  /** @remarks Configuration for weekday selection field */
  weekdayConfig = new FormControlConfig({
    name: 'weekday',
    label: 'Wochentag',
    required: true,
    errorMessage: 'Bitte wählen Sie einen Wochentag',
    errorMessageRequired: 'Dieses Feld ist ein Pflichtfeld. Bitte ausfüllen.',
    placeholder: 'Wochentag wählen',
    type: 'select',
    options: [
      { value: 'montag', label: 'Montag' },
      { value: 'dienstag', label: 'Dienstag' },
      { value: 'mittwoch', label: 'Mittwoch' },
      { value: 'donnerstag', label: 'Donnerstag' },
      { value: 'freitag', label: 'Freitag' },
      { value: 'samstag', label: 'Samstag' },
    ],
  });

  /** @remarks Configuration for time range selection field */
  timeRangeConfig = new FormControlConfig({
    name: 'timeRange',
    label: 'Zeit',
    required: true,
    errorMessage: 'Bitte wählen Sie einen Zeitraum',
    errorMessageRequired: 'Dieses Feld ist ein Pflichtfeld. Bitte ausfüllen.',
    placeholder: 'Zeitraum wählen',
    type: 'select',
    options: [
      { value: '8-10', label: '8:00 - 10:00 Uhr' },
      { value: '10-12', label: '10:00 - 12:00 Uhr' },
      { value: '12-14', label: '12:00 - 14:00 Uhr' },
      { value: '14-16', label: '14:00 - 16:00 Uhr' },
      { value: '16-18', label: '16:00 - 18:00 Uhr' },
      { value: '18-20', label: '18:00 - 19:00 Uhr' },
    ],
  });

  /** @remarks Configuration for email input field */
  emailConfig = new FormControlConfig({
    name: 'email',
    label: 'E-Mail Adresse',
    required: true,
    errorMessage: 'Bitte geben Sie eine gültige E-Mail-Adresse im Format name@beispiel.de an',
    errorMessageRequired: 'Dieses Feld ist ein Pflichtfeld. Bitte ausfüllen.',
    placeholder: 'E-Mail Adresse eingeben',
    type: 'email',
  });

  /** @remarks Configuration for privacy consent checkbox field */
  privacyConfig = new FormControlConfig({
    name: 'privacyConsent',
    label: 'Datenschutzbestimmung',
    required: true,
    errorMessage: 'Sie müssen die Datenschutzbestimmungen akzeptieren',
    errorMessageRequired: 'Dieses Feld ist ein Pflichtfeld. Bitte ausfüllen.',
    type: 'checkbox',
    checkboxText:
      'Ich habe die aktuellen <a href="https://extapi.gw.inexio.net/publicDocuments.php/getDocument?id=84" target="_blank">Datenschutzbestimmungen</a> gelesen und akzeptiere diese.',
  });

  /**
   * Creates an instance of ContactComponent.
   *
   * @param {ApiService} apiService - Service for API communication
   * @param {DialogService} dialogService - Service for managing modal dialogs
   * @param {DataLayerService} dataLayerService - Service for analytics tracking
   */
  constructor(
    private apiService: ApiService,
    public dialogService: DialogService,
    private dataLayerService: DataLayerService
  ) {}

  /**
   * Component initialization lifecycle hook.
   *
   * @remarks Initializes the reactive form and sets up form value change listeners
   * for dynamic field behavior based on contact method selection.
   */
  ngOnInit(): void {
    this.initializeForm();
    this.setupFormValueChanges();
  }

  /**
   * Initializes the reactive form with all form controls and validators.
   *
   * @remarks Creates the main form group with all necessary form controls,
   * including validation rules for each field. Uses custom validation patterns
   * for name and phone number fields.
   *
   */
  private initializeForm(): void {
    // Build form using controls with explicit configurations and validators
    this.contactForm = new FormGroup({
      salutation: new FormControl('', Validators.required),
      firstName: new FormControl('', [Validators.required, Validators.maxLength(80), Validators.pattern(VALIDATION_PATTERNS.namePattern)]),
      lastName: new FormControl('', [Validators.required, Validators.maxLength(80), Validators.pattern(VALIDATION_PATTERNS.namePattern)]),
      contactMethod: new FormControl('', Validators.required),
      phoneNumber: new FormControl('', [Validators.pattern(VALIDATION_PATTERNS.phonePattern)]),
      weekday: new FormControl('', Validators.required),
      timeRange: new FormControl('', Validators.required),
      email: new FormControl('', [Validators.email]),
      privacyConsent: new FormControl(false, Validators.requiredTrue),
    });
  }

  /**
   * Sets up form value change listeners for dynamic behavior.
   *
   * @remarks Subscribes to contact method changes to dynamically adjust
   * form validation and field requirements based on user selection.
   *
   */
  private setupFormValueChanges(): void {
    // Listen for changes in the contact method
    this.contactForm.get('contactMethod')?.valueChanges.subscribe(method => {
      this.applyFormState(method);
    });
  }

  /**
   * Applies the appropriate form state based on the contact method.
   *
   * @remarks Dynamically adjusts form validation and field requirements
   * based on whether the user selects phone or written contact method.
   * Resets conditional controls and applies method-specific validation.
   *
   * @param {string} contactMethod - The selected contact method ('phone' or 'written')
   */
  private applyFormState(contactMethod: string): void {
    // Get form controls
    const phoneNumberControl = this.contactForm.get('phoneNumber');
    const weekdayControl = this.contactForm.get('weekday');
    const timeRangeControl = this.contactForm.get('timeRange');
    const emailControl = this.contactForm.get('email');

    // First, reset all conditional controls
    this.resetAllConditionalControls();

    // Then apply state-specific settings
    if (contactMethod === 'phone') {
      // Apply telefonisch state
      this.applyPhoneState(phoneNumberControl, weekdayControl, timeRangeControl, emailControl);
    } else if (contactMethod === 'written') {
      // Apply schriftlich state
      this.applyWrittenState(phoneNumberControl, weekdayControl, timeRangeControl, emailControl);
    }
  }

  /**
   * Resets all conditional controls to their base state.
   *
   * @remarks Clears validators from conditional form controls while preserving
   * base validators. Updates validation status for all affected controls.
   *
   */
  private resetAllConditionalControls(): void {
    const controls = [
      this.contactForm.get('phoneNumber'),
      this.contactForm.get('weekday'),
      this.contactForm.get('timeRange'),
      this.contactForm.get('email'),
    ];

    // Clear validators but preserve base validators
    this.resetControlValidators(controls[0], [Validators.pattern(VALIDATION_PATTERNS.phonePattern)]);
    this.resetControlValidators(controls[1]);
    this.resetControlValidators(controls[2]);
    this.resetControlValidators(controls[3], [Validators.email]);

    // Update validation status for all controls
    controls.forEach(control => control?.updateValueAndValidity());
  }

  /**
   * Applies the 'telefonisch' (phone) state to the form.
   *
   * @remarks Sets required validators for phone-related fields (phone number,
   * weekday, time range) and resets the email field. Updates validation for all
   * affected controls.
   *
   * @param {AbstractControl | null} phoneNumberControl - Phone number form control
   * @param {AbstractControl | null} weekdayControl - Weekday selection form control
   * @param {AbstractControl | null} timeRangeControl - Time range selection form control
   * @param {AbstractControl | null} emailControl - Email input form control
   */
  private applyPhoneState(
    phoneNumberControl: AbstractControl | null,
    weekdayControl: AbstractControl | null,
    timeRangeControl: AbstractControl | null,
    emailControl: AbstractControl | null
  ): void {
    // Set required validators for phone-related fields
    phoneNumberControl?.addValidators(Validators.required);
    weekdayControl?.addValidators(Validators.required);
    timeRangeControl?.addValidators(Validators.required);

    // Reset email field
    emailControl?.setValue('');

    // Update validation
    phoneNumberControl?.updateValueAndValidity();
    weekdayControl?.updateValueAndValidity();
    timeRangeControl?.updateValueAndValidity();
    emailControl?.updateValueAndValidity();
  }

  /**
   * Applies the 'schriftlich' (written) state to the form.
   *
   * @remarks Sets required validators for email field and resets phone-related
   * fields (phone number, weekday, time range). Updates validation for all
   * affected controls.
   *
   * @param {AbstractControl | null} phoneNumberControl - Phone number form control
   * @param {AbstractControl | null} weekdayControl - Weekday selection form control
   * @param {AbstractControl | null} timeRangeControl - Time range selection form control
   * @param {AbstractControl | null} emailControl - Email input form control
   */
  private applyWrittenState(
    phoneNumberControl: AbstractControl | null,
    weekdayControl: AbstractControl | null,
    timeRangeControl: AbstractControl | null,
    emailControl: AbstractControl | null
  ): void {
    // Set required validators for email
    emailControl?.addValidators([Validators.required, Validators.maxLength(100)]);

    // Reset phone-related fields
    phoneNumberControl?.setValue('');
    weekdayControl?.setValue('');
    timeRangeControl?.setValue('');

    // Update validation
    phoneNumberControl?.updateValueAndValidity();
    weekdayControl?.updateValueAndValidity();
    timeRangeControl?.updateValueAndValidity();
    emailControl?.updateValueAndValidity();
  }

  /**
   * Resets control validators while maintaining base validators.
   *
   * @remarks Clears all validators from a form control and optionally
   * adds back specified base validators. Used for dynamic form validation.
   *
   * @param {AbstractControl | null} control - The form control to reset
   * @param {ValidatorFn[]} [baseValidators=[]] - Base validators to maintain
   */
  private resetControlValidators(control: AbstractControl | null, baseValidators: ValidatorFn[] = []): void {
    if (control) {
      control.clearValidators();
      if (baseValidators.length > 0) {
        control.addValidators(baseValidators);
      }
    }
  }

  /**
   * Handles form submission.
   *
   * @remarks Validates the form and submits data to the API if valid.
   * Shows success or error dialogs based on submission result and tracks
   * form completion events for analytics. Resets form on successful submission.
   *
   */
  onSubmit(): void {
    if (this.contactForm.valid) {
      this.submissionStatus = 'submitting';
      const formData = this.contactForm.value;

      this.apiService.post<{ workflowID: string }>('/ticket', formData).subscribe({
        next: () => {
          this.submissionStatus = 'success';
          this.dialogService.openDialog({
            headline: 'Vielen Dank für Ihre Anfrage!',
            text: 'Wir melden uns zu Ihrem gewählten Zeitpunkt zurück.',
          });

          // Map contact method to expected dataLayer format
          const contactMethod = this.contactForm.get('contactMethod')?.value;
          const contactType = contactMethod === 'phone' ? 'telefonisch' : 'schriftlich';
          this.dataLayerService.pushFormSuccessEvent(contactType);

          this.contactForm.reset();
        },
        error: () => {
          this.submissionStatus = 'error';
          this.dialogService.openDialog({
            headline: 'Ein Fehler ist aufgetreten',
            text: 'Ihre Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns telefonisch.',
          });
        },
      });
    } else {
      // Mark all fields as touched to trigger validation
      Object.keys(this.contactForm.controls).forEach(key => {
        const control = this.contactForm.get(key);
        control?.markAsTouched();
      });
    }
  }
}
